import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';
import { DataColumn } from '../../../models';
import { UserActionComponent } from '../../../shared/components/user-action/user-action.component';
import { AddUserComponent } from './add-user/add-user.component';
import { DetailsUserComponent } from './details-user/details-user.component';
import { AgencyUser, AgencyUsersService } from '../../../services/agency-users.service';
import { PermissionService } from '../../../services/permission.service';
import { AgencyPermission } from '../../../shared/utils/agency-rbac';
import { UserRoleCellComponent } from './cells/user-role-cell.component';
import { UserStatusCellComponent } from './cells/user-status-cell.component';
import { FlagService } from '../../../shared/services/flag.service';
import { FlagType } from '../../../shared/utils/enums';

/** "Gestion des collaborateurs" — ouvert par l'un des droits comptes (créer / supprimer / modifier les droits) ; chaque action reste gardée par son propre droit. */
@Component({
  selector: 'app-agency-users',
  templateUrl: './agency-users.component.html',
  styleUrl: './agency-users.component.scss'
})
export class AgencyUsersComponent implements OnInit, OnDestroy {

  readonly columns: DataColumn[] = [
    { header: 'Prénom', fieldName: 'firstName', dataType: 'string', cellWidth: '12%', unClickable: true },
    { header: 'Nom', fieldName: 'lastName', dataType: 'string', cellWidth: '12%', upperCase: true, unClickable: true },
    { header: 'Email', fieldName: 'mail', dataType: 'string', cellWidth: '17%', unClickable: true },
    { header: 'Fonction', fieldName: 'fonction', dataType: 'string', cellWidth: '12%', unClickable: true },
    { header: 'Rôle', fieldName: 'roleLabel', dataType: 'component', component: UserRoleCellComponent, cellWidth: '15%', unClickable: true },
    { header: 'Statut', fieldName: 'statusLabel', dataType: 'component', component: UserStatusCellComponent, cellWidth: '11%', unClickable: true },
    { header: 'Dernière connexion', fieldName: 'lastLogin', dataType: 'string', cellWidth: '12%', unClickable: true },
    { header: 'Actions', fieldName: '', dataType: 'component', component: UserActionComponent, cellWidth: '9%', unClickable: true },
  ] as DataColumn[];

  /** Téléphone : le tableau à 8 colonnes est remplacé par une carte par collaborateur. */
  isMobile = false;
  loading = false;
  keyword = '';
  personsList: any[] = [];
  personsListFiltered: any[] = [];
  totalItems = 0;
  private refreshSub?: Subscription;
  private breakpointSub?: Subscription;

  readonly AgencyPermission = AgencyPermission;

  constructor(private dialog: MatDialog,
              private usersService: AgencyUsersService,
              private flag: FlagService,
              private breakpoints: BreakpointObserver,
              public permissions: PermissionService) {
  }

  ngOnInit() {
    this.breakpointSub = this.breakpoints.observe('(max-width: 768px)').subscribe((state) => {
      this.isMobile = state.matches;
    });
    this.loadUsers();
    this.refreshSub = this.usersService.refresh$.subscribe(() => {
      this.loadUsers();
    });
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
    this.breakpointSub?.unsubscribe();
  }

  openInviteDialog() {
    const dialogRef = this.dialog.open(AddUserComponent, {
      width: '720px',
      autoFocus: false,
      data: null,
    });
    dialogRef.afterClosed().subscribe((changed) => {
      if (changed) {
        this.loadUsers();
      }
    });
  }

  pagination(_event: any) {
    // The agency has a handful of collaborators: the whole list is shown.
  }

  handleRowClick = (row: any) => {
    this.dialog.open(DetailsUserComponent, {
      width: '666px',
      height: '100vh',
      position: { right: '0' },
      backdropClass: 'bg-transparent',
      data: row,
    });
  };

  searchUser(event: any) {
    this.keyword = (event?.target?.value ?? '').trim().toLowerCase();
    this.applyFilter();
  }

  private applyFilter() {
    const k = this.keyword;
    this.personsListFiltered = !k
      ? [...this.personsList]
      : this.personsList.filter((p: any) =>
          [p.mail, p.firstName, p.lastName, p.fonction, p.roleLabel, p.statusLabel]
            .some((v) => (v ?? '').toString().toLowerCase().includes(k))
        );
    this.totalItems = this.personsListFiltered.length;
  }

  loadUsers() {
    this.loading = true;
    this.usersService.list().subscribe({
      next: (users: AgencyUser[]) => {
        this.personsList = (users ?? []).map((u) => this.toRow(u));
        this.applyFilter();
        this.loading = false;
      },
      error: (err) => {
        this.personsList = [];
        this.applyFilter();
        this.loading = false;
        this.flag.displayFlag(AgencyUsersService.errorMessage(err, 'Impossible de charger les collaborateurs.'), FlagType.error);
      },
    });
  }

  private toRow(u: AgencyUser) {
    return {
      ...u,
      mail: u.email,
      numTel: u.phoneNumber,
      lastLogin: u.lastLoginAt ? AgencyUsersComponent.formatDateTime(u.lastLoginAt) : (u.status === 'PENDING' ? '—' : 'Jamais'),
    };
  }

  static formatDateTime(value?: string): string {
    if (!value) {
      return '';
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return value;
    }
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}
